import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';
import {PieceKindSchema} from './editorial.js';

/**
 * Contratos da porta interna.
 *
 * "Interna" aqui significa: chamada por outro serviço da própria operação, e
 * não por um navegador. O n8n agenda a geração diária e acompanha o resultado.
 *
 * A diferença que importa: **não existe bearer neste caminho**. Um token no
 * workflow do n8n seria um segredo de longa duração guardado num JSON que
 * várias pessoas conseguem exportar. A identidade vem do certificado da
 * conexão, que não se copia num copiar e colar.
 */

export const EditorialRunStatusSchema = z.enum([
  'queued',
  'running',
  'partial',
  'ready',
  'failed',
]);
export type EditorialRunStatus = z.infer<typeof EditorialRunStatusSchema>;

/**
 * O pedido do agendador: SÓ a data.
 *
 * `accountId` saiu daqui, e a remoção é o conserto de um defeito medido. Ele
 * era texto livre de 1 a 120 caracteres, sem vínculo com conta nenhuma, e a
 * execução era identificada no banco por `(account_id, local_date)`. Abrir o
 * mesmo dia com dois nomes diferentes gerava DUAS execuções sobre AS MESMAS
 * seis peças, o dono aprovava duas versões da mesma peça sem ter como saber, e
 * a peça ia ao ar duas vezes.
 *
 * Quem sabe de qual conta este processo fala é o processo, pelo ambiente em que
 * ele subiu, e não quem bate na porta. `strictObject` completa a cerca: mandar
 * `accountId` agora é `422`, e não um campo ignorado em silêncio, então um
 * workflow antigo apontado para a API nova falha alto em vez de parecer que
 * funcionou.
 */
export const CreateEditorialRunRequestSchema = z.strictObject({
  /** Data no fuso da operação, e não do servidor. */
  localDate: z.iso.date(),
});
export type CreateEditorialRunRequest = z.infer<
  typeof CreateEditorialRunRequestSchema
>;

export const CreateEditorialRunResponseSchema = z.strictObject({
  runId: UuidV4Schema,
  status: EditorialRunStatusSchema,
  /** `true` quando a execução já existia: o n8n repetiu a chamada. */
  alreadyExisted: z.boolean(),
});
export type CreateEditorialRunResponse = z.infer<
  typeof CreateEditorialRunResponseSchema
>;

/**
 * O que aconteceu com UMA peça dentro da execução.
 *
 * Os DOIS identificadores vêm, e não um só. Quem tem desfecho é a peça, e o
 * item carrega duas: o Reel e a Story do mesmo horário. Antes, a lista trazia
 * só `contentItemId` e o worker escrevia ali o id da PEÇA, então o runbook que
 * tentasse abrir o item por aquele identificador não achava nada, e as seis
 * linhas do dia pareciam seis itens quando são três.
 */
export const InternalEditorialRunItemSchema = z.strictObject({
  contentItemId: UuidV4Schema,
  contentPieceId: UuidV4Schema,
  contentVersionId: UuidV4Schema.nullable(),
  /**
   * A espécie da peça, do vocabulário do editorial.
   *
   * Era `z.enum(['reel', 'story'])` até 2026-09-06, e a lista escrita à mão
   * ficou para trás quando a peça de foto nasceu (migração 0077): o relatório
   * de todo dia com item de notícia carrega `photo` nesta chave. Nada quebrou
   * em produção porque a rota interna devolve o objeto sem revalidar, e é
   * exatamente isso que torna o defeito perigoso: o schema mente em silêncio, e
   * quem um dia validar do outro lado reprova o relatório inteiro de um dia bom.
   */
  kind: PieceKindSchema,
  /**
   * `blocked` é desfecho terminal e legítimo: a peça foi gerada e os portões
   * recusaram. Diferente de `failed`, que é a esteira não ter conseguido
   * produzir nada.
   *
   * `settled` é a peça que esta execução NÃO tocou porque ela já tinha saído
   * das mãos da esteira: o dono aprovou, pediu mudança ou rejeitou. Ela existe
   * porque reexecutar um dia `partial` é o caminho que o runbook manda seguir
   * para recuperar um dia incompleto, e refazer nesse caminho uma peça já
   * decidida criaria uma segunda versão aprovável da MESMA peça, com a primeira
   * já enfileirada para publicar. Post duplicado na conta do dono não se
   * desfaz.
   */
  outcome: z.enum([
    'awaiting_approval',
    'blocked',
    'settled',
    'failed',
    'pending',
  ]),
  failureCode: z.string().min(1).max(80).nullable(),
});
export type InternalEditorialRunItem = z.infer<
  typeof InternalEditorialRunItemSchema
>;

/**
 * Visão da execução pela porta INTERNA.
 *
 * Distinta do `EditorialRunSchema` do editorial, que é o resumo com contadores
 * que a task 1 congelou. Esta aqui carrega o desfecho peça a peça, que é o que
 * o n8n e o runbook precisam para reabrir uma falha específica. Duas visões do
 * mesmo assunto, com nomes diferentes, é melhor que uma inchada.
 */
export const InternalEditorialRunSchema = z.strictObject({
  runId: UuidV4Schema,
  accountId: z.string().min(1),
  localDate: z.iso.date(),
  status: EditorialRunStatusSchema,
  startedAt: IsoUtcDateTimeSchema.nullable(),
  finishedAt: IsoUtcDateTimeSchema.nullable(),
  items: z.array(InternalEditorialRunItemSchema),
  /** Peças que a operação precisa olhar. Vazio é o normal. */
  alerts: z.array(z.string().min(1)),
});
export type InternalEditorialRunDto = z.infer<
  typeof InternalEditorialRunSchema
>;

/**
 * Identidade de um serviço interno.
 *
 * O certificado é apresentado pela conexão, e a porta só diz QUEM é. Autorizar
 * é decisão de quem chama, e separar as duas coisas evita o erro clássico de
 * um autenticador que também decide permissão e vai virando um emaranhado.
 */
export interface InternalServiceIdentity {
  service: 'n8n';
  environment: 'staging' | 'production';
}

export interface PeerCertificate {
  subjectAltName: string;
  fingerprint256: string;
  issuerFingerprint256: string;
  /** Data de expiração, no formato que o Node entrega. */
  validTo: string;
}

export interface InternalServiceAuth {
  authenticate(request: {
    peerCertificate: PeerCertificate;
    environment: 'staging' | 'production';
  }): Promise<InternalServiceIdentity>;
}
